import { BinaryLegsProps, BinaryLegsEditorProps } from './BinaryLegs.types';

/**
 * 기존 데이터 형식을 새로운 형식으로 변환
 * 
 * @param originalProps 기존 컴포넌트의 props
 * @returns 변환된 새로운 컴포넌트용 props
 */
export function adaptBinaryLegsProps(originalProps: any): BinaryLegsProps {
  // 기본값 설정 (빈 객체로 초기화)
  const originalContent = originalProps?.content || {};
  const originalData = originalProps?.data || {};
  const originalComponentProps = originalProps?.componentProps || {};
  
  // 스타일 병합 (깊은 복사로 병합)
  const style = {
    ...(originalProps?.style || {}),
    ...(originalComponentProps?.style || {})
  };
  
  // 인라인 스타일과 CSS 클래스 통합
  if (originalProps.defaultStyle) {
    Object.assign(style, originalProps.defaultStyle);
  }

  // className 설정
  const className = originalProps?.className || originalComponentProps?.className || 'binary-legs-component';

  // 타이틀 설정
  const title = originalComponentProps?.title || originalContent?.title || originalData?.title || '후원 레그';
  
  // 디버그 모드 설정
  const debugMode = typeof originalComponentProps?.debugMode !== 'undefined' 
    ? originalComponentProps.debugMode 
    : false;

  // 변환된 props 반환
  const adaptedProps: BinaryLegsProps = {
    id: originalProps?.id || '',
    style,
    className,
    content: { 
      ...originalContent 
    },
    data: originalData,
    componentProps: {
      ...originalComponentProps,
      title,
      debugMode,
      // 스킨 관련 속성 추가
      skin: originalComponentProps?.skin || 'basic',
      emptyText: originalComponentProps?.emptyText || '데이터가 없습니다',
      loadingText: originalComponentProps?.loadingText || '조직도를 불러오는 중입니다...',
      errorText: originalComponentProps?.errorText || '데이터를 불러오는데 실패했습니다',
      loginRequiredText: originalComponentProps?.loginRequiredText || '로그인이 필요한 서비스입니다.',
      showNavigationButtons: originalComponentProps?.showNavigationButtons !== false,
      showBreadcrumb: originalComponentProps?.showBreadcrumb !== false,
      showDebugMode: originalComponentProps?.showDebugMode || false,
      showZoomControls: originalComponentProps?.showZoomControls !== false,
      showNodePV: originalComponentProps?.showNodePV !== false,
      showNodeRank: originalComponentProps?.showNodeRank !== false,
      showNodeGrade: originalComponentProps?.showNodeGrade !== false,
      showNodePhone: originalComponentProps?.showNodePhone !== false,
      showNodeCenter: originalComponentProps?.showNodeCenter !== false,
      showNodeGroupPV: originalComponentProps?.showNodeGroupPV !== false,
      showMoreButton: originalComponentProps?.showMoreButton !== false,
      initialZoom: originalComponentProps?.initialZoom || 1,
      minZoom: originalComponentProps?.minZoom || 0.3,
      maxZoom: originalComponentProps?.maxZoom || 2.5,
      lineColor: originalComponentProps?.lineColor || '#3f51b5',
      nodeBackgroundColor: originalComponentProps?.nodeBackgroundColor || '#ffffff',
      nodeBorderColor: originalComponentProps?.nodeBorderColor || '#e0e0e0',
      nodeShadow: originalComponentProps?.nodeShadow || '0 2px 4px rgba(0,0,0,0.1)',
      titleBackgroundColor: originalComponentProps?.titleBackgroundColor || '#3f51b5',
      titleTextColor: originalComponentProps?.titleTextColor || '#ffffff',
      contentTextColor: originalComponentProps?.contentTextColor || '#333333',
      bubbleBadgeColor: originalComponentProps?.bubbleBadgeColor || '#2196f3',
      bubbleBadgeTextColor: originalComponentProps?.bubbleBadgeTextColor || '#ffffff',
      buttonPrimaryColor: originalComponentProps?.buttonPrimaryColor || '#3f51b5',
      buttonSecondaryColor: originalComponentProps?.buttonSecondaryColor || '#6c757d',
      buttonSuccessColor: originalComponentProps?.buttonSuccessColor || '#28a745',
      buttonDangerColor: originalComponentProps?.buttonDangerColor || '#dc3545',
      containerBackgroundColor: originalComponentProps?.containerBackgroundColor || '#ffffff',
      breadcrumbColor: originalComponentProps?.breadcrumbColor || '#666666',
      breadcrumbActiveColor: originalComponentProps?.breadcrumbActiveColor || '#3f51b5'
    },
    pathVariables: originalProps?.pathVariables || {}
  };

  // 원본 props의 모든 속성 중 이미 처리한 속성 제외하고 나머지 복사
  const excludeKeys = ['id', 'content', 'style', 'className', 'data', 'componentProps', 'pathVariables'];
  
  Object.keys(originalProps).forEach(key => {
    if (!excludeKeys.includes(key)) {
      adaptedProps[key] = originalProps[key];
    }
  });

  return adaptedProps;
}

/**
 * 에디터용 데이터 형식 변환
 * 
 * @param originalProps 기존 컴포넌트의 props
 * @returns 변환된 에디터 컴포넌트용 props
 */
export function adaptBinaryLegsEditorProps(originalProps: any): BinaryLegsEditorProps {
  // 기본 props 변환 사용
  const adaptedProps = adaptBinaryLegsProps(originalProps);
  
  // 에디터 관련 추가 props
  return {
    ...adaptedProps,
    isSelected: originalProps.isSelected || false,
    isPreview: originalProps.isPreview || false,
    viewMode: originalProps.viewMode || 'pc',
    updateComponent: originalProps.updateComponent || (() => {})
  };
} 